import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Image, FlatList } from 'react-native';
import type { ArticleFeedProps, ArticleProps, BEEventProps, BracketCompetitionProps, BracketProps, CompanyProps, CompetitionProps, CompetitionResultTypeProps, CompetitionTypeProps, EventProps, LeagueProps, PodcastProps, PublicPlayerProps, SquaresCompetitionProps } from '../../types';
import type { CompanyMemberProps } from '../../Authenticator/api/types';
import { CompanyProfileApi, CompanyProfileHelpers } from './api';
import { Icons, Toggle } from '../../Components';
import PlayerCard from '../PlayerCard';
import CompetitionCard from '../../Engage/components/CompetitionCard';
import BracketCompetitionCard from '../../Engage/components/BracketCompetitionCard';
import SquaresCompetitionCard from '../../Engage/components/SquaresCompetitionCard';
import ArticleCard from '../ArticleCard';
import PodcastCard from '../PodcastCard';
import moment from 'moment-mini';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type CompanyProfileProps = {
    company_id:string,
    padding_insets?:{ top:number, bottom:number },
    onSelectCompetition:(c:CompetitionProps) => void,
    onSelectBracketCompetition:(bc:BracketCompetitionProps) => void,
    onSelectSquaresCompetition:(sc:SquaresCompetitionProps) => void,
    onArticleSelect:(article:ArticleProps) => void,
    onEvent?:(be_event:BEEventProps) => void,
    onSelectPodcast:(pod:PodcastProps) => void,
    onSelectPlayer:(player:PublicPlayerProps) => void,
    onBack?:() => void,
    onShareCompany?:(company:CompanyProps) => void
}

const co_profile_sections = ['gap','info','members','podcasts','engage_toggle', 'competitions','feeds']
const CompanyProfile = ({ company_id, padding_insets, onSelectCompetition, onSelectBracketCompetition, onSelectSquaresCompetition, onArticleSelect, onEvent, onSelectPodcast, onSelectPlayer, onBack, onShareCompany }:CompanyProfileProps) => {
    const Colors = useColors();
    
    const [ module_size, setModuleSize ] = useState({ height:700, width:300 });
    const [ podcast_data, setPodcastData ] = useState<{
        podcast_loading:boolean,
        podcasts:PodcastProps[]
    }>({
        podcast_loading:false,
        podcasts:[]
    });
    const { podcasts } = podcast_data;

    const [ company_data, setCompanyData ] = useState<{
        company_loading:boolean
        active_toggle:string,
        company?:CompanyProps,
        company_members:CompanyMemberProps[],
        players:PublicPlayerProps[]
    }>({
        company_loading: false,
        company_members: [],
        players:[],
        active_toggle: 'competitions'
    });
    const { company_loading, company_members, company, players, active_toggle } = company_data;

    const [ competition_data, setCompetitionData ] = useState<{
        competition_loading:boolean,
        competition_types:CompetitionTypeProps[],
        leagues:LeagueProps[],
        brackets:BracketProps[],
        competition_result_types:CompetitionResultTypeProps[],
        competitions:CompetitionProps[],
        events:EventProps[],
        bracket_competitions:BracketCompetitionProps[],
        squares_competitions:SquaresCompetitionProps[],
        offset:number
    }>({
        competition_loading: false,
        offset: 0,
        competitions:[],
        leagues:[],
        competition_types:[],
        brackets: [],
        events:[],
        competition_result_types:[],
        bracket_competitions:[],
        squares_competitions: [],
    })
    const { competition_loading, competitions, bracket_competitions, brackets, squares_competitions, events, competition_result_types, competition_types, leagues } = competition_data;
    const sorted_comps = CompanyProfileHelpers.sortComps(competitions, bracket_competitions, squares_competitions);

    const [ feed_data, setFeedData ] = useState<{
        feed_loading:boolean,
        article_feeds:ArticleFeedProps[],
        articles:ArticleProps[],
        feed_offset:number
    }>({
        feed_loading: false,
        article_feeds:[],
        articles:[],
        feed_offset:0
    });
    const { feed_loading, article_feeds, articles } = feed_data;

    useEffect(() => {
        CompanyProfileApi.setEnvironment();
        if(!company_id){ return }
        getCompanyDataFromServer(company_id);
        getPodcastsFromServer(company_id, 0);
    },[company_id]);

    useEffect(() => {
        switch(active_toggle){
            case 'competitions':
                getCompsFromServer(company_id, 0);
                return
            case 'feeds':
                getFeedFromServer(company_id, 0);
                return
            default: return
        }
    },[active_toggle]);

    const getPodcastsFromServer = async(id:string, offset:number) => {
        setPodcastData({ ...podcast_data, podcast_loading:true });
        const ps = await CompanyProfileApi.getPodcastsByCompanyId(id, offset);
        setPodcastData({
            ...podcast_data,
            podcast_loading: false,
            podcasts: ps
        })
    }

    const getFeedFromServer = async(id:string, offset:number) => {
        setFeedData({ ...feed_data, feed_loading:true });
        const feeds = await CompanyProfileApi.getArticleFeedsByCompanyId(id);
        const arts = await CompanyProfileApi.getArticlesByCompanyId(id, offset);
        setFeedData({
            ...feed_data,
            feed_loading: false,
            article_feeds: feeds,
            articles: arts,
            feed_offset:offset
        })
    }

    const getCompsFromServer = async(id:string, offset:number) => {
        setCompetitionData({ ...competition_data, competition_loading: true });
        const comps = await CompanyProfileApi.getCompetitionsByCompanyId(id, offset, 'active');
        const bcs = await CompanyProfileApi.getBracketCompetitionsByCompanyId(id, offset, 'active');
        const scs = await CompanyProfileApi.getSquaresCompsByCompanyId(id, 'active');
        const opts = await CompanyProfileApi.getCompetitionOptions();
        const lgs = await CompanyProfileApi.getLeagues();
        if(!opts){ return alert('Unable to get competition details') }
        const bks = await CompanyProfileApi.getBracketsByIds(bcs.map(bc => bc.bracket_id));
        const es = await CompanyProfileApi.getEventsByEventIds(scs.map(sc => sc.event_id));
        setCompetitionData({ 
            ...competition_data,
            competition_loading: false,
            competitions:comps.filter(c => !c.invite_only),
            bracket_competitions: bcs,
            squares_competitions: scs,
            leagues:lgs,
            brackets: bks,
            competition_result_types: opts.competition_result_types,
            events: es,
            competition_types: opts.competition_types,
            offset
        })
    }

    const getCompanyDataFromServer = async(id:string) => {
        if(onEvent){
            onEvent({
                event_name:'company_view',
                event_data: {
                    company_id:id,
                    view_type:'profile',
                    view_location: 'profile'
                },
                level:2
            })
        }

        setCompanyData({ ...company_data, company_loading:true });
        const c_resp = await CompanyProfileApi.getCompanyById(id);
        if(!c_resp){ return alert('Unable to get company details.  Please try again later') }
        let ps = await CompanyProfileApi.getPlayersByPlayerIds(c_resp.company_members.map(cm => cm.player_id))
        setCompanyData({
            ...company_data,
            company_loading:false,
            company: c_resp.company,
            company_members: c_resp.company_members,
            players: ps,
        });
    }

    const renderMembers = (data: { item:CompanyMemberProps, index:number }) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        return (
            <View float style={{ margin:4 }}>
                <PlayerCard
                    player={player}
                    width={150}
                    
                    alignment='vertical'
                    show_follow
                    onSelectPlayer={onSelectPlayer}
                    onFollowingUpdate={(pf) => console.log(pf)}
                />
            </View>
        )
    }

    const renderArticles = (data:{ item:ArticleProps, index:number }) => {
        const article_feed = article_feeds.find(af => af.article_feed_id == data.item.article_feed_id);
        let article_width = (module_size.width - 60) / 2
        if(article_width > 240){ article_width = 240 }
        return (
            <View float style={{ margin:4 }}>
                <ArticleCard
                    article_feed={article_feed}
                    article={data.item}
                    width={article_width}
                    onSelectArticle={onArticleSelect}
                    onEvent={onEvent}
                />
            </View>
        )
    }

    const renderPodcasts = (data: { item:PodcastProps }) => {
        let pod_width = (module_size.width) / 3
        if(pod_width > 200){ pod_width = 200 }
        return (
            <View float style={{ margin:4 }}>
                <PodcastCard
                    podcast={data.item}
                    width={pod_width}
                    onSelectPodcast={onSelectPodcast}

                />
            </View>
        )
    }

    const renderComps = (data: { item:{ type: 'competition'|'squares_competition'|'bracket_competition', id:string, start_time:any }, index:number }) => {
        switch(data.item.type){
            case 'competition':
                const competition = competitions.find(c => c.competition_id == data.item.id);
                if(!competition){ return <></> }
                const competition_type = competition_types.find(ct => ct.competition_type_id == competition.competition_type_id);
                if(!competition_type){ return <></> }
                const competition_result_type = competition_result_types.find(ct => ct.competition_result_type_id == competition.competition_result_type_id);
                if(!competition_result_type){ return <></> }
                return (
                    <View float style={{ margin:5 }}>
                        <CompetitionCard
                            competition={competition}
                            company={company}
                            competition_type={competition_type}
                            competition_result_type={competition_result_type}
                            onCompetitionSelect={onSelectCompetition}
                        />
                    </View>
                )
            case 'bracket_competition':
                const bracket_competition = bracket_competitions.find(bc => bc.bracket_competition_id == data.item.id);
                if(!bracket_competition){ return <></> }
                const crt = competition_result_types.find(ct => ct.competition_result_type_id == bracket_competition.competition_result_type_id);
                if(!crt){ return <></> }
                const bracket = brackets.find(b => b.bracket_id == bracket_competition.bracket_id);
                const league = leagues.find(l => l.league_id == bracket?.league_id);
                return (
                    <View float style={{ margin:5 }}>
                        <BracketCompetitionCard
                            bracket_competition={bracket_competition}
                            competition_result_type={crt}
                            company={company}
                            bracket={bracket}
                            league={league}
                            onCompetitionSelect={onSelectBracketCompetition}
                        />
                    </View>
                )
            case 'squares_competition':
                const squares_competition = squares_competitions.find(sc => sc.sq_comp_id == data.item.id);
                if(!squares_competition){ return <></> }
                const event = events.find(e => e.event_id == squares_competition.event_id);
                return (
                    <View float style={{ margin:5 }}>
                        <SquaresCompetitionCard
                            squares_competition={squares_competition}
                            onSelectCompetition={onSelectSquaresCompetition}
                            company={company}
                            event={event}
                        />
                    </View>
                )
            default: return <></>
        }
    }
    
    const renderSections = (data: { item:string, index:number }) => {
        switch(data.item){
            case 'gap':
                return (
                    <View transparent style={{ height:300 }} />
                )
            case 'info':
                if(!company){ return <></> }
                return (
                    <View style={{ borderTopRightRadius: 22, borderTopLeftRadius:22 }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20, borderTopRightRadius: 22, borderTopLeftRadius:22 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>{company.company_name}</Text>
                                <Text style={{ marginTop:4 }} theme='description'>{company.company_description}</Text>
                            </View>
                            <Button disabled style={{ backgroundColor:Colors.absolutes.highlights.highlight500 }}>
                                <Text size={10} theme='h1' color={Colors.text.white} textAlign='center'>{company.type.toUpperCase()}</Text>
                            </Button>
                        </View>
                    </View>
                )
            case 'members':
                if(!company){ return <></> }
                return (
                     <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                            <View transparent>
                                <Text theme='h1'>Members</Text>
                                <Text style={{ marginTop:3 }} theme='description'>The following are members of {company.company_name}</Text>
                            </View>
                            <View transparent style={{ padding:10, flex:1, alignItems:'flex-end' }}>
                                <Text theme='h1'>{company_members.length}</Text>
                            </View>
                        </View>
                        <View type='body' style={{ padding:10 }}>
                            <View type='row'>
                                <View style={{ flex:1 }}>
                                    <FlatList
                                        key='company_members'
                                        horizontal
                                        showsHorizontalScrollIndicator={false}
                                        data={company_members}
                                        renderItem={renderMembers}
                                        keyExtractor={(item) => item.player_id.toString()}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                )
            case 'podcasts':
                if(!company){ return <></> }
                if(podcasts.length == 0){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Podcasts</Text>
                                <Text style={{ marginTop:3 }} theme='description'>The following are podcasts hosted by {company.company_name}</Text>
                            </View>
                        <View transparent style={{ padding:10 }}>
                            <Text theme='h2'>{podcasts.length}</Text>
                        </View>
                    </View>
                    <View type='body' style={{ padding:10 }}>
                        <View type='row'>
                            <View style={{ flex:1 }}>
                                <FlatList
                                    key='company_pods'
                                    horizontal
                                    showsHorizontalScrollIndicator={false}
                                    data={podcasts}
                                    renderItem={renderPodcasts}
                                    keyExtractor={(item) => item.podcast_id.toString()}
                                />
                            </View>
                        </View>
                    </View>
                </View>
                )
            case 'engage_toggle':
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{flex:1, paddingLeft:10}}>
                                <Text theme='h1'>Engage with {company?.company_name}</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Use the toggle to find ways to engage</Text>
                            </View>
                        </View>
                        <View style={{ padding:10 }}>
                            <Toggle
                                options={[
                                    { key: 'competitions', label: 'Comps' },
                                    { key: 'feeds', label: 'Feeds' }
                                ]}
                                selected_option={active_toggle}
                                onSelectOption={(option) => setCompanyData({ ...company_data, active_toggle: option })}
                            />
                        </View>
                    </View>
                )
            case 'competitions':
                if(active_toggle != 'competitions'){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        {competition_loading ?
                        <ActivityIndicator size='large' color={Colors.text.h1} style={{ padding:20, alignSelf:'center' }} />
                        :<></>}
                        <FlatList
                            key={'comps_list'}
                            data={sorted_comps.sort((a,b) => a.start_time.unix() - b.start_time.unix())}
                            renderItem={renderComps}
                            keyExtractor={(item) => `${item.id}:${item.type}`}
                        />
                    </View>
                )
            case 'feeds':
                if(active_toggle!='feeds'){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        {feed_loading ?
                        <ActivityIndicator size='large' color={Colors.text.h1} style={{ padding:20, alignSelf:'center' }} />
                        :<></>}
                        <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                            {articles.sort((a,b) => moment(b.create_datetime).unix() - moment(a.create_datetime).unix()).map((a,i) => {
                                return renderArticles({ item: a, index:i })
                            })}
                        </View>
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { height, width } = ev.nativeEvent.layout
            setModuleSize({ width, height });
        }}>
            <View style={{ position:'absolute', top:0, left:0, right:0, alignItems:'center' }} nativeID='profile_image'>
                <Image
                    source={{ uri: company?.company_image?.url }}
                    style={{ width: module_size.width, height: module_size.width, borderBottomRightRadius:50, borderBottomLeftRadius:50, backgroundColor:Colors.text.white }}
                    resizeMode='cover'
                />
            </View>
             <FlatList
                data={co_profile_sections}
                key='co_profile_sections_list'
                keyExtractor={(item) => item}
                onRefresh={() => getCompanyDataFromServer(company_id)}
                refreshing={company_loading}
                renderItem={renderSections}
            />
            {onBack ?
            <Button 
                float
                style={{ position:'absolute', top:padding_insets?.top ?? 0, left:0, height:50, width:50, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                onPress={() => onBack()}>
                <Icons.ChevronIcon direction='left' color={Colors.text.h1} size={14} />
            </Button> 
            :<></>}
            {company && onShareCompany?
            <Button 
                float
                style={{ position:'absolute', top:padding_insets?.top ?? 0, right:0, height:50, width:50, borderRadius:100, justifyContent:'center', alignItems:'center' }}
                onPress={() => onShareCompany(company)}>
                <Icons.ShareIcon color={Colors.text.h1} size={14} />
            </Button> 
            :<></>}
        </View>
    )
}

export default CompanyProfile

